import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronDown, Menu, Moon, Sun, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../contexts/ThemeContext';
import { trackCTA } from '../../utils/metaPixel';
import MadeInIndiaBadge from '../Brand/MadeInIndiaBadge';
import { GGJLogoFull } from '../Brand/GGJLogo';
import { BRAND_LOGO_DISPLAY } from '../../config/brandLogos';
import { LanguageSelector } from '../LanguageSelector';

import { getB2BAppUrl, openB2BApp } from '../../config/b2bAppUrl';

import { marketingPathForView } from './marketingViewPaths';


type HomeNavItem =
  | { kind: 'anchor'; labelKey: string; hash: string }
  | { kind: 'route'; labelKey: string; view: string };

const HOME_NAV_ITEMS: HomeNavItem[] = [
  { kind: 'anchor', labelKey: 'home.nav.home', hash: '#hero' },
  { kind: 'anchor', labelKey: 'home.nav.interactiveDemo', hash: '#interactive-demo' },
  { kind: 'anchor', labelKey: 'home.nav.jobCopilot', hash: '#job-copilot' },
  { kind: 'anchor', labelKey: 'home.nav.problem', hash: '#problem' },
  { kind: 'anchor', labelKey: 'home.nav.features', hash: '#features-overview' },
  { kind: 'anchor', labelKey: 'home.nav.socialProof', hash: '#social-proof' },
  { kind: 'anchor', labelKey: 'home.nav.solutions', hash: '#who-its-for' },
  { kind: 'route', labelKey: 'home.nav.blog', view: 'blog' },
  { kind: 'route', labelKey: 'home.nav.careers', view: 'careers' },
  { kind: 'route', labelKey: 'home.nav.pricing', view: 'pricing' },
  { kind: 'route', labelKey: 'home.nav.contact', view: 'contact-us' },
];

const MARKETING_NAV_ITEMS: HomeNavItem[] = [
  { kind: 'route', labelKey: 'home.nav.blog', view: 'blog' },
  { kind: 'route', labelKey: 'home.nav.pricing', view: 'pricing' },
  { kind: 'route', labelKey: 'home.nav.careers', view: 'careers' },
  { kind: 'route', labelKey: 'home.nav.contact', view: 'contact-us' },
  { kind: 'route', labelKey: 'home.nav.about', view: 'about-us' },
];

type FunnelDropdownItem = { labelKey: string; view: string };

type FunnelNavItem =
  | { kind: 'route'; labelKey: string; view: string }
  | { kind: 'dropdown'; id: string; labelKey: string; items: FunnelDropdownItem[] };

const FUNNEL_NAV_ITEMS: FunnelNavItem[] = [
  {
    kind: 'dropdown',
    id: 'features',
    labelKey: 'home.nav.featuresMenu',
    items: [
      { labelKey: 'home.nav.allFeatures', view: 'features-overview' },
      { labelKey: 'home.nav.featureResume', view: 'feature-resume-optimizer' },
      { labelKey: 'home.nav.featureMatching', view: 'feature-smart-matching' },
      { labelKey: 'home.nav.featureInterview', view: 'feature-interview-prep' },
    ],
  },
  {
    kind: 'dropdown',
    id: 'solutions',
    labelKey: 'home.nav.whyGoGrabJob',
    items: [
      { labelKey: 'home.nav.problem', view: 'problem' },
      { labelKey: 'home.nav.solutions', view: 'who-its-for' },
    ],
  },
  { kind: 'route', labelKey: 'home.nav.liveJobs', view: 'job-board' },
  { kind: 'route', labelKey: 'home.nav.successStories', view: 'success-stories' },
  { kind: 'route', labelKey: 'home.nav.forBusiness', view: 'business' },
];

export const MARKETING_NAV_TOP_OFFSET =
  'pt-[calc(3.75rem+env(safe-area-inset-top,0px))]';

export interface MarketingLandingNavProps {
  onNavigate?: (view: string) => void;
  /** Highlight the active route (e.g. careers on /careers). */
  activeView?: string;
  /** funnel = acquisition nav; home = legacy anchors; marketing = inner pages */
  variant?: 'home' | 'marketing' | 'funnel';
}

const MarketingLandingNav: React.FC<MarketingLandingNavProps> = ({
  onNavigate,
  activeView,
  variant = 'marketing',
}) => {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const reduceMotion = useReducedMotion();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const navItems = variant === 'home' ? HOME_NAV_ITEMS : MARKETING_NAV_ITEMS;
  const isFunnel = variant === 'funnel';
  // Wordmark color is driven by CSS (.home-landing-nav__logo .ggj-logo-full__label) so it
  // stays readable on light marketing surfaces and is not overridden by page-level dark:text-white.
  const navUtilityBtnClass =
    'home-landing-nav__utility-btn flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 transition-colors touch-manipulation';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleNavClick = (view: string) => {
    closeMobileMenu();
    setOpenDropdown(null);
    if (view === 'signup' || view === 'login') trackCTA(`marketing_nav_${view}`);
    if (view === 'careers') trackCTA('marketing_nav_careers');
    if (view === 'business') {
      trackCTA('marketing_nav_business');
      openB2BApp();
      return;
    }
    onNavigate?.(view);
  };

  const handleRouteLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, view: string) => {
    event.preventDefault();
    handleNavClick(view);
  };

  const scrollToSection = (hash: string) => {
    closeMobileMenu();
    const id = hash.replace(/^#/, '');
    // Keep hash on the home path (`/#hero`) so refresh does not hit a missing `/hero` route.
    const targetUrl = `/#${id}`;

    const scrollTarget = () => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', targetUrl);
    };

    const el = document.getElementById(id);
    if (el) {
      scrollTarget();
      return;
    }
    onNavigate?.('home');
    window.setTimeout(scrollTarget, 150);
  };

  const linkClass = (view?: string) =>
    [
      'home-landing-nav__link',
      view && activeView === view ? 'home-landing-nav__link--active' : '',
    ].join(' ');

  return (
    <>
      <nav
        aria-label="Main navigation"
        className={`home-landing-nav home-landing-nav--${isFunnel ? 'funnel' : variant} fixed top-0 left-0 right-0 z-[200] pt-[env(safe-area-inset-top)] font-sans antialiased transition-all duration-300 ${
          isScrolled ? 'home-landing-nav--scrolled' : ''
        }`}
      >
        <div
          className={`home-landing-nav__bar mx-auto w-full max-w-7xl items-center px-4 py-2 md:px-8${
            isFunnel ? ' home-landing-nav__bar--funnel' : ' flex'
          }`}
        >
          <div className="home-landing-nav__start flex min-w-0 items-center">
            <div className="home-landing-nav__brand-cluster">
              {isFunnel ? (
                <MadeInIndiaBadge
                  size="sm"
                  variant="pill"
                  className="home-landing-nav__india-badge"
                />
              ) : null}
              <button
                type="button"
                onClick={() => {
                  if (isFunnel && window.location.pathname !== '/') {
                    handleNavClick('home');
                    return;
                  }
                  if (variant === 'home' || variant === 'funnel') {
                    scrollToSection('#hero');
                    return;
                  }
                  handleNavClick('home');
                }}
                className="home-landing-nav__logo flex shrink-0 items-center transition-opacity touch-manipulation hover:opacity-85"
              >
                <GGJLogoFull
                  iconSize={BRAND_LOGO_DISPLAY.marketingNavIcon}
                  fontSize={BRAND_LOGO_DISPLAY.marketingNavFont}
                  boostMark={false}
                  className="home-landing-nav__logo-lockup"
                />
              </button>
            </div>

            {!isFunnel ? (
              <ul className="home-landing-nav__links hidden list-none p-0 m-0 xl:flex">
                {navItems.map((item) => (
                  <li key={item.kind === 'anchor' ? item.hash : item.view}>
                    {item.kind === 'anchor' ? (
                      <a
                        href={item.hash}
                        className="home-landing-nav__link"
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToSection(item.hash);
                        }}
                      >
                        {t(item.labelKey)}
                      </a>
                    ) : (
                      <button
                        type="button"
                        className={linkClass(item.view)}
                        onClick={() => handleNavClick(item.view)}
                      >
                        {t(item.labelKey)}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {isFunnel ? (
            <ul className="home-landing-nav__links home-landing-nav__links--funnel hidden list-none p-0 m-0 lg:flex">
              {FUNNEL_NAV_ITEMS.map((item) => (
                <li key={item.kind === 'dropdown' ? item.id : item.view} className="relative">
                  {item.kind === 'dropdown' ? (
                    <div
                      className="home-landing-nav__dropdown"
                      onMouseEnter={() => setOpenDropdown(item.id)}
                      onMouseLeave={() => setOpenDropdown((current) => (current === item.id ? null : current))}
                    >
                      <button
                        type="button"
                        className="home-landing-nav__link home-landing-nav__link--dropdown"
                        aria-expanded={openDropdown === item.id}
                        aria-haspopup="menu"
                        onClick={() =>
                          setOpenDropdown((current) => (current === item.id ? null : item.id))
                        }
                      >
                        {t(item.labelKey)}
                        <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                      </button>
                      {openDropdown === item.id ? (
                        <div className="home-landing-nav__dropdown-panel" role="menu">
                          {item.items.map((child) => (
                            <a
                              key={child.view}
                              href={marketingPathForView(child.view)}
                              role="menuitem"
                              className="home-landing-nav__dropdown-item"
                              onClick={(event) => handleRouteLinkClick(event, child.view)}
                            >
                              {t(child.labelKey)}
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <a
                      href={item.view === 'business' ? getB2BAppUrl() : marketingPathForView(item.view)}
                      className={linkClass(item.view)}
                      target={item.view === 'business' ? '_blank' : undefined}
                      rel={item.view === 'business' ? 'noopener noreferrer' : undefined}
                      onClick={(event) => handleRouteLinkClick(event, item.view)}
                    >
                      {t(item.labelKey)}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          ) : null}

          <div
            className={`home-landing-nav__actions flex shrink-0 items-center gap-3 sm:gap-4${
              isFunnel ? '' : ' ml-auto'
            }`}
          >
            {!isFunnel ? (
              <a
                href={getB2BAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="home-landing-nav__text-action hidden 2xl:inline-flex"
                onClick={() => trackCTA('marketing_nav_business')}
              >
                {t('home.nav.forBusiness')}
              </a>
            ) : null}
            <button type="button" onClick={() => handleNavClick('login')} className="home-landing-nav__text-action hidden sm:inline-flex">
              {t('home.nav.login')}
            </button>
            <button
              type="button"
              onClick={() => handleNavClick('signup')}
              className={`home-landing-nav__signup hidden ${isFunnel ? 'lg' : 'xl'}:inline-flex`}
            >
              {isFunnel ? t('home.nav.getStartedFree', 'Get Started Free') : t('home.nav.getStarted')}
              <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
            </button>

            <div
              className={`home-landing-nav__utilities hidden items-center gap-0.5 ${
                isFunnel ? 'lg:flex' : 'xl:flex'
              }`}
            >
              <LanguageSelector variant="default" buttonClass={navUtilityBtnClass} />
              <button
                type="button"
                onClick={toggleTheme}
                className={navUtilityBtnClass}
                aria-label={isDark ? t('layout.ariaSwitchToLight') : t('layout.ariaSwitchToDark')}
              >
                {isDark ? <Sun className="h-5 w-5" strokeWidth={1.75} /> : <Moon className="h-5 w-5" strokeWidth={1.75} />}
              </button>
            </div>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className={`home-landing-nav__menu-btn flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg ${
                isFunnel ? 'lg:hidden' : 'xl:hidden'
              }`}
              aria-label={t('home.nav.menu')}
              aria-expanded={mobileMenuOpen}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.2 }}
              className={`fixed inset-0 z-[110] bg-black/40 backdrop-blur-sm ${
                isFunnel ? 'lg:hidden' : 'xl:hidden'
              }`}
              onClick={closeMobileMenu}
              aria-hidden
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: reduceMotion ? 0 : 0.25 }}
              className={`home-landing-nav__drawer fixed top-0 right-0 bottom-0 z-[120] flex max-h-[100dvh] flex-col overflow-y-auto bg-white shadow-xl pt-[env(safe-area-inset-top)] pb-[max(0.5rem,env(safe-area-inset-bottom))] ${
                isFunnel ? 'lg:hidden' : 'xl:hidden'
              }`}
            >
              <div className="home-landing-nav__drawer-header flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
                <GGJLogoFull
                iconSize={BRAND_LOGO_DISPLAY.marketingNavIcon}
                fontSize={BRAND_LOGO_DISPLAY.marketingNavFont}
                boostMark={false}
                className="home-landing-nav__logo-lockup"
              />
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  className="home-landing-nav__drawer-close flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-500 touch-manipulation hover:text-gray-700"
                  aria-label={t('layout.ariaCloseMenu')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-3">
                {isFunnel
                  ? FUNNEL_NAV_ITEMS.flatMap((item) => {
                      if (item.kind === 'dropdown') {
                        return item.items.map((child) => (
                          <a
                            key={child.view}
                            href={marketingPathForView(child.view)}
                            onClick={(event) => handleRouteLinkClick(event, child.view)}
                            className={`home-landing-nav__drawer-link flex min-h-[44px] w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium touch-manipulation hover:bg-gray-100 ${
                              activeView === child.view
                                ? 'home-landing-nav__drawer-link--active text-[#6C47FF]'
                                : 'text-gray-700'
                            }`}
                          >
                            {t(child.labelKey)}
                          </a>
                        ));
                      }
                      return (
                        <a
                          key={item.view}
                          href={item.view === 'business' ? getB2BAppUrl() : marketingPathForView(item.view)}
                          target={item.view === 'business' ? '_blank' : undefined}
                          rel={item.view === 'business' ? 'noopener noreferrer' : undefined}
                          onClick={(event) => handleRouteLinkClick(event, item.view)}
                          className={`home-landing-nav__drawer-link flex min-h-[44px] w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium touch-manipulation hover:bg-gray-100 ${
                            activeView === item.view
                              ? 'home-landing-nav__drawer-link--active text-[#6C47FF]'
                              : 'text-gray-700'
                          }`}
                        >
                          {t(item.labelKey)}
                        </a>
                      );
                    })
                  : navItems.map((item) =>
                      item.kind === 'anchor' ? (
                        <a
                          key={item.hash}
                          href={item.hash}
                          onClick={(e) => {
                            e.preventDefault();
                            scrollToSection(item.hash);
                          }}
                          className="home-landing-nav__drawer-link flex min-h-[44px] items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 touch-manipulation hover:bg-gray-100"
                        >
                          {t(item.labelKey)}
                        </a>
                      ) : (
                        <button
                          key={item.view}
                          type="button"
                          onClick={() => handleNavClick(item.view)}
                          className={`home-landing-nav__drawer-link flex min-h-[44px] w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium touch-manipulation hover:bg-gray-100 ${
                            activeView === item.view
                              ? 'home-landing-nav__drawer-link--active text-[#6C47FF]'
                              : 'text-gray-700'
                          }`}
                        >
                          {t(item.labelKey)}
                        </button>
                      ),
                    )}
                {!isFunnel ? (
                  <a
                    href={getB2BAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      closeMobileMenu();
                      trackCTA('marketing_nav_business');
                    }}
                    className="home-landing-nav__drawer-link flex min-h-[44px] w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 touch-manipulation hover:bg-gray-100"
                  >
                    {t('home.nav.forBusiness')}
                  </a>
                ) : null}
              </nav>
              <div className="home-landing-nav__drawer-footer flex flex-shrink-0 flex-col gap-2 border-t border-gray-200 px-3 py-3">
                <div className="flex items-center gap-0.5 pb-1">
                  <LanguageSelector variant="default" buttonClass={navUtilityBtnClass} />
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className={navUtilityBtnClass}
                    aria-label={isDark ? t('layout.ariaSwitchToLight') : t('layout.ariaSwitchToDark')}
                  >
                    {isDark ? <Sun className="h-5 w-5" strokeWidth={1.75} /> : <Moon className="h-5 w-5" strokeWidth={1.75} />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => handleNavClick('login')}
                  className="home-landing-nav__drawer-login w-full min-h-[44px] rounded-lg bg-gray-100 px-3 py-2.5 text-sm font-medium text-gray-900 transition-colors touch-manipulation hover:bg-gray-200"
                >
                  {t('home.nav.login')}
                </button>
                <button type="button" onClick={() => handleNavClick('signup')} className="home-landing-nav__signup w-full min-h-[44px]">
                  {isFunnel ? t('home.nav.getStartedFree', 'Get Started Free') : t('home.nav.getStarted')}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MarketingLandingNav;
export { HOME_NAV_ITEMS };
